<template>
  <aside class="project-one-right-rail" qaid="project-one-right-rail">
    <project-one-right-rail-header
      :base-properties="baseProperties"
      :group-id="groupId"
      :is-back-button-enabled="true"
      @on-backpress="returnToPreviousRailView"
    />

    <section class="project-one-right-rail__body" qaid="project-one-right-rail-body">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-8">
        <pdl-media-object v-if="selectedColor">
          <div slot="media">
            <img qaid="project-one-swatch-view-thumbnail" :src="imageUrl" />
          </div>
          <div slot="content">
            <h4 qaid="project-one-swatch-view-name" class="text-sm font-medium" v-html="selectedColor.description" />
            <div qaid="project-one-swatch-view-description" class="text-gray-60 text-sm mt-1">
              <span v-html="selectedColor.marketingCopy" />
            </div>
          </div>
        </pdl-media-object>
        <div class="swatches-content">
          <div
            qaid="project-one-swatch-view-swatches-copy"
            class="text-xs mt-5 md:mt-0 lg:mt-5"
            v-html="bp('availableColors')"
          />
          <pdl-swatch-group
            class="mt-2"
            :options="swatchGroupOptions"
            :change-callback="onSwatchSelected"
            :initial-selected-option="swatchSelectedOption"
            radio-name="swatch-options"
          />
        </div>
      </div>
    </section>

    <project-one-right-rail-footer
      :base-properties="baseProperties"
      :button-action="confirmSelection"
      button-icon="check"
      :button-icon-left="true"
      :button-primary="true"
      :button-text="bp('done')"
    />
  </aside>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import {PdlMediaObject} from '@pedal/pdl-media-object';
import {PdlSwatchGroup} from '@pedal/pdl-swatch';
import ProjectOneRightRailFooter from '@/projectOne/components/rightRail/components/ProjectOneRightRailFooter';
import ProjectOneRightRailHeader from '@/projectOne/components/rightRail/components/ProjectOneRightRailHeader';

export default {
  components: {PdlMediaObject, PdlSwatchGroup, ProjectOneRightRailFooter, ProjectOneRightRailHeader},

  computed: {
    colorChoices() {
      return this.groupId && this.contentData ? this.contentData.choices : null;
    },

    contentData() {
      if (this.activeRailData?.content?.groupId && Array.isArray(this.moduleGroups)) {
        return this.moduleGroups.find((group) => group.id === this.activeRailData.content.groupId);
      }

      return null;
    },

    groupId() {
      return this.contentData?.id;
    },

    topTierPackageId() {
      return this.contentData?.topTierPackageId;
    },

    imageUrl() {
      return this.selectedColor?.imageThumbnailReference
        ? `${this.baseImageUrl}${this.selectedColor.imageThumbnailReference}?wid=100&hei=80&fit=stretch,1`
        : null;
    },

    selectedColor() {
      const selection = this.selections.find((selection) => selection.groupId === this.groupId);
      const selectedColor =
        this.colorChoices.find((color) => color.id === selection.choiceId) ||
        this.colorChoices.find((color) => color.id === this.contentData?.defaultChoiceId) ||
        this.colorChoices[0] ||
        null;
      return selectedColor;
    },

    swatchGroupOptions() {
      return this.colorChoices?.map((color) => this.convertColorToSwatchOption(color));
    },

    swatchSelectedOption() {
      return this.convertColorToSwatchOption(this.selectedColor);
    },

    ...mapGetters('projectOne', ['activeRailData']),
    ...mapState('projectOne', ['baseImageUrl', 'moduleGroups', 'railDataStack', 'selections']),
  },

  methods: {
    confirmSelection() {
      this.$notify({
        type: 'success',
        message: this.bp('added'),
      });

      this.returnToInitialRailView();
    },

    convertColorToSwatchOption(color) {
      return color
        ? {
            id: color.id,
            groupId: this.contentData?.id,
            topTierPackageId: this.topTierPackageId,
            name: color.description?.replace(/\W/g, ''), // Remove special characters for name
            imageUrl: `${this.baseImageUrl}${color.imageThumbnailReference}?wid=40&hei=40&fit=stretch,1`,
          }
        : null;
    },

    onSwatchSelected(newSwatch) {
      // Only call the backend when the swatch selected is different than the existing one
      let isDifferentSelection = true;
      if (Array.isArray(this.selections)) {
        const selection = this.selections.find((selection) => selection.groupId === newSwatch.groupId);
        if (selection) isDifferentSelection = selection.choiceId !== newSwatch.id;
      }
      if (isDifferentSelection) {
        this.updateSelection({
          selections: [
            {groupId: newSwatch.groupId, choiceId: newSwatch.id, topTierPackageId: newSwatch.topTierPackageId},
          ],
          buildId: this.$route.params.buildId,
          module: 'paint-scheme',
          bpData: this.bpData,
        });
      }
    },

    returnToInitialRailView() {
      while (this.railDataStack?.length > 1) {
        this.popRailDataStack();
      }
    },

    returnToPreviousRailView() {
      if (this.railDataStack?.length > 1) {
        this.popRailDataStack();
      } else {
        this.$emit('on-backpress');
      }
    },

    ...mapActions('projectOne', ['updateSelection']),
    ...mapMutations('projectOne', ['popRailDataStack']),
  },
};
</script>

<style lang="scss">
@import '@pedal/pdl-swatch/dist/SwatchGroup.scss';
@import '@pedal/pdl-swatch/dist/Swatch.scss';

.swatches-content {
  ul {
    &.pdl-swatches {
      --swatch-items-across: 20;
    }
  }
}
</style>
